<template>
  <v-container fluid grid-list-lg>
    <AccountTerminalDebug :terminal="selectedTerminal"></AccountTerminalDebug>

    <v-layout row wrap ma-0 pa-0 align-end v-if="$route.name === 'ChargeACH'">
      <v-flex>
        <v-layout row wrap justify-end>
          <span class="red--text">* Red Fields Required for Processing</span>
        </v-layout>
      </v-flex>
    </v-layout>

    <v-form ref="form" @submit.prevent>
      <template v-if="$route.query.hasOwnProperty('transaction') || $route.query.hasOwnProperty('rerun')">
        <v-flex xs12>
          <v-checkbox
            label="New Payment Info"
            v-model="newPaymentInfo"
            color="primary"
            box
            hide-details
            class="ma-0"
            @click.native.stop="clearToken"
          ></v-checkbox>
        </v-flex>
      </template>

      <v-layout row wrap v-if="$route.name === 'ChargeCard'">
        <v-flex xs12 sm4>
          <v-text-field
            v-if="parentId"
            label="Card Holder Name"
            v-model="parentDetails.billing_name"
            append-icon="fa-user"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Card Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :rules="nameRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_name')"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            v-if="parentId"
            label="Card Number"
            v-model="parentCardNumber"
            :append-icon="(!cardType || cardType==='unknown') ? 'fa-credit-card' : ''"
            box
            hide-details
            disabled
          >
            <img slot="append" :src="'/static/cards/' + cardType + '.svg'" height="24px" v-if="cardType && cardType!=='unknown'" />
          </v-text-field>
          <v-text-field
            v-else
            label="Card Number"
            v-model="fields.card_number"
            :append-icon="(!cardType || cardType==='unknown') ? 'fa-credit-card' : ''"
            :mask="cardMask"
            :rules="cardRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_number')"
          >
            <img slot="append" :src="'/static/cards/' + cardType + '.svg'" height="24px" v-if="cardType && cardType!=='unknown'" />
          </v-text-field>
        </v-flex>
        <v-flex xs4>
          <v-text-field
            v-if="parentId"
            label="Expiration"
            v-model="parentExpiration"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Expiration"
            type="tel"
            v-model="fields.card_expiration"
            append-icon="fa-calendar"
            mask="## / ##"
            :rules="expirationRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_expiration')"
          ></v-text-field>
        </v-flex>
        <v-flex xs4>
          <v-text-field
            v-if="parentId"
            label="CVV"
            value="***"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="CVV"
            type="test"
            v-model="fields.card_cvv"
            :mask="cvvMask"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_cvv')"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <AmountThreshold
            v-model="fields.amount"
            :disabled="waivePrevious"
            :required="!waivePrevious"
            :error-messages="getRequiredField('amount')"
          ></AmountThreshold>
        </v-flex>
        <v-flex xs3 sm2>
          <v-text-field
            v-if="selectedTerminal.is_cashdiscount"
            label="Price Adj Fee"
            v-model="fields.fee"
            :disabled="fields.waive_fee"
            :rules="feeRules"
            append-icon="fa-dollar"
            box
            hide-details
            readonly
          ></v-text-field>
        </v-flex>
        <v-flex xs4 v-if="selectedTerminal.processor_id === 3 || selectedTerminal.processor_id === 6">
          <v-select
            label="Recurring"
            v-model="fields.recurring"
            :items="recurringTypes"
            append-icon="fa-refresh"
            box
            hide-details
            :disabled="waivePrevious"
          ></v-select>
        </v-flex>
        <v-flex xs4 v-if="fields.recurring !== 'none'">
          <v-text-field
            v-if="waivePrevious"
            label="Recurring Start Date"
            v-model="formattedRecurringStartDate"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-menu
            v-else
            v-model="startDateModal"
            ref="startDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.recurring_start_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="Recurring Start Date"
              slot="activator"
              v-model="formattedRecurringStartDate"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.recurring_start_date"
              :min="minimumRecurringStartDate"
              @input="$refs.startDateModal.save(fields.recurring_start_date)"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs4 v-if="fields.recurring !== 'none'">
          <v-text-field
            v-if="waivePrevious || fields.recurring_ongoing"
            label="Recurring End Date"
            v-model="formattedRecurringEndDate"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-menu
            v-else
            v-model="endDateModal"
            ref="endDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.recurring_end_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="Recurring End Date"
              slot="activator"
              v-model="formattedRecurringEndDate"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.recurring_end_date"
              :allowed-dates="recurringAllowedEndDates"
              :min="minimumRecurringEndDate"
              @input="$refs.endDateModal.save(fields.recurring_end_date)"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs6 sm2 v-if="fields.recurring !== 'none'">
          <v-text-field
            label="# of Payments"
            type="number"
            v-model="fields.recurring_payments"
            :disabled="!fields.recurring_start_date || waivePrevious || fields.recurring_ongoing"
            append-icon="fa-hashtag"
            @input="handleChange('payments')"
            box
            hide-details
            :readonly="fields.recurring_ongoing"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2 v-if="fields.recurring !== 'none'">
          <v-checkbox
            label="Ongoing"
            v-model="fields.recurring_ongoing"
            color="primary"
            box
            hide-details
            :disabled="waivePrevious"
          ></v-checkbox>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="$route.name === 'ChargeACH'">
        <v-flex xs6 sm4>
          <v-text-field
            v-if="parentId"
            label="Account Holder Name"
            v-model="parentDetails.billing_name"
            append-icon="fa-user"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Account Holder Name"
            v-model="fields.card_name"
            append-icon="fa-user"
            :rules="nameRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('card_name')"
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            v-if="parentId"
            label="Routing Number"
            v-model="parentDetails.ach_routing_number"
            append-icon="fa-exchange"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Routing Number"
            v-model="fields.ach_routing_number"
            append-icon="fa-exchange"
            :rules="routingRules"
            mask="#########"
            box
            hide-details
            required
            :error-messages="getRequiredField('ach_routing_number')"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4 pb-0>
          <v-text-field
            v-if="parentId"
            label="Account Number"
            v-model="parentAccountNumber"
            append-icon="fa-institution"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Account Number"
            v-model="fields.ach_account_number"
            append-icon="fa-institution"
            :rules="accountRules"
            box
            hide-details
            required
            :error-messages="getRequiredField('ach_account_number')"
          ></v-text-field>
          <AchFindInfoModal></AchFindInfoModal>
        </v-flex>
        <v-flex xs6 sm4>
          <v-select
            v-if="parentId"
            label="Type"
            v-model="parentDetails.ach_account_type"
            :items="achTypes"
            box
            hide-details
            disabled
          ></v-select>
          <v-select
            v-else
            label="Type"
            v-model="fields.ach_type"
            :items="achTypes"
            box
            hide-details
            required
            :error-messages="getRequiredField('ach_type')"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm4>
          <AmountThreshold
            v-model="fields.amount"
            required
          ></AmountThreshold>
        </v-flex>
        <v-flex xs4>
          <v-select
            label="Recurring"
            v-model="fields.recurring"
            :items="recurringTypes"
            append-icon="fa-refresh"
            box
            hide-details
          ></v-select>
        </v-flex>
        <v-flex xs4 v-if="fields.recurring !== 'none'">
          <v-menu
            v-model="startDateModal"
            ref="startDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.recurring_start_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="Recurring Start Date"
              v-model="formattedRecurringStartDate"
              slot="activator"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.recurring_start_date"
              :min="minimumRecurringStartDate"
              @input="$refs.startDateModal.save(fields.recurring_start_date)"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs4 v-if="fields.recurring !== 'none'">
          <v-text-field
            v-if="fields.recurring_ongoing"
            label="Recurring End Date"
            v-model="formattedRecurringEndDate"
            append-icon="fa-calendar"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-menu
            v-else
            v-model="endDateModal"
            ref="endDateModal"
            :close-on-content-click="false"
            :nudge-right="40"
            :return-value.sync="fields.recurring_end_date"
            full-width
            lazy
            min-width="290px"
            offset-y
            transition="scale-transition"
          >
            <v-text-field
              label="Recurring End Date"
              v-model="formattedRecurringEndDate"
              slot="activator"
              append-icon="fa-calendar"
              box
              hide-details
              readonly
            ></v-text-field>
            <v-date-picker
              v-model="fields.recurring_end_date"
              :allowed-dates="recurringAllowedEndDates"
              :min="minimumRecurringEndDate"
              @input="$refs.endDateModal.save(fields.recurring_end_date)"
            ></v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs6 sm2 v-if="fields.recurring !== 'none'">
          <v-text-field
            label="# of Payments"
            type="number"
            v-model="fields.recurring_payments"
            @input="handleChange('payments')"
            :disabled="!fields.recurring_start_date"
            append-icon="fa-hashtag"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2 v-if="fields.recurring !== 'none'">
          <v-checkbox
            label="Ongoing"
            v-model="fields.recurring_ongoing"
            color="primary"
            box
            hide-details
            :disabled="waivePrevious"
          ></v-checkbox>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs12 sm4>
          <v-text-field
            v-if="waivePrevious"
            label="Address"
            v-model="fields.billing_address"
            append-icon="fa-home"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Address"
            v-model="fields.billing_address"
            append-icon="fa-home"
            box
            hide-details
            required
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            v-if="waivePrevious"
            label="City"
            v-model="fields.billing_city"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="City"
            v-model="fields.billing_city"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <StateField
            v-model="fields.billing_state"
            :disabled="waivePrevious"
            :textfield="fields.billing_country !== 'USA'"
          ></StateField>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            v-if="waivePrevious"
            label="Postal Code"
            v-model="fields.billing_postalcode"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Postal Code"
            v-model="fields.billing_postalcode"
            :rules="$route.name === 'ChargeCard' ? postalcodeRules : []"
            box
            hide-details
            :error-messages="$route.name === 'ChargeCard' ? getRequiredField('billing_postalcode') : ''"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            v-if="waivePrevious"
            label="Phone"
            v-model="fields.billing_phone"
            mask="(###) ###-####"
            append-icon="fa-phone"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Phone"
            v-model="fields.billing_phone"
            mask="(###) ###-####"
            append-icon="fa-phone"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm4>
          <v-text-field
            v-if="waivePrevious"
            label="Email"
            v-model="fields.billing_email"
            append-icon="fa-at"
            box
            hide-details
            disabled
          ></v-text-field>
          <v-text-field
            v-else
            label="Email"
            v-model="fields.billing_email"
            append-icon="fa-at"
            :rules="emailRules"
            box
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm2>
          <CountryField
            v-model="fields.billing_country"
            :disabled="waivePrevious || $route.name === 'ChargeACH'"
            icon="fa-globe"
          ></CountryField>
        </v-flex>
        <v-flex xs6 sm2>
          <v-text-field
            label="Customer PO"
            v-model="fields.customer_po"
            append-icon="fa-file-text-o"
            box
            hide-details
            :disabled="waivePrevious"
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Used to relate transactions to your management software.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
      </v-layout>

      <v-divider class="primary"></v-divider>

      <v-layout row wrap>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 1"
            v-model="fields.custom_field_1"
            append-icon="fa-edit"
            maxlength="30"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 2"
            v-model="fields.custom_field_2"
            append-icon="fa-edit"
            maxlength="30"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs6 sm4>
          <v-text-field
            label="Custom 3"
            v-model="fields.custom_field_3"
            append-icon="fa-edit"
            maxlength="30"
            box
            hide-details
          >
            <div slot="append">
              <Lightbulb>
                <template v-slot:message>
                  <span>Any data you put into custom fields 1, 2 or 3 will print on the receipt.</span>
                </template>
              </Lightbulb>
            </div>
          </v-text-field>
        </v-flex>
        <v-flex xs12>
          <v-textarea
            label="Notes (Internal Use Only will not print on customer receipt)"
            v-model="fields.notes"
            box
            hide-details
            height
            rows="1"
          ></v-textarea>
        </v-flex>
      </v-layout>
<!--
      <v-layout row justify-end class="my-1">
        <div class="mx-5">
          <h2>Sub Total: </h2>
          <p>
            ${{ parseFloat(priceAdjustmentFee.total_amount).toFixed(2) }}
          </p>
        </div>
        <div class="mx-5" v-if="selectedTerminal.is_cashdiscount && $route.name === 'ChargeCard'">
          <h2>Price Adjustment Fee: </h2>
          <p>
              ${{ parseFloat(priceAdjustmentFee.cash_discount).toFixed(2) }}
          </p>
        </div>
        <div class="mx-5">
          <h2>Total: </h2>
          <p>
              ${{ parseFloat(priceAdjustmentFee.total_amount_with_cash_discount).toFixed(2) }}
          </p>
        </div>
      </v-layout> -->

      <v-divider class="primary"></v-divider>

      <v-layout row wrap justify-space-between align-center fill-height class="mt-3">
        <template v-if="$route.name === 'ChargeCard'">
          <TerminalSelect ref="terminal" v-model="selectedTerminal"></TerminalSelect>

          <template v-if="waiveHealthCard || waiveLevel">
            <v-flex xs12 sm4 class="dark warning" pa-4>
              <v-layout row wrap justify-center>
                {{ waiveMessage }}
              </v-layout>
            </v-flex>
          </template>
          <template v-if="waivePrevious">
            <v-flex xs12 :sm8="!waiveHealthCard" :sm4="waiveHealthCard">
              <v-layout row wrap justify-end>
                <v-btn @click="validateBeforeProcessing" :disabled="isProcessing">Process</v-btn>
              </v-layout>
            </v-flex>
          </template>
          <template v-else>
            <v-flex xs12 :sm8="!waiveHealthCard && !waiveLevel" :sm4="waiveHealthCard || waiveLevel">
              <v-layout row wrap justify-end>
                <v-btn @click="validateBeforeProcessing" :disabled="isProcessing">Process</v-btn>
                <v-btn @click="reset" v-if="!parentId">clear</v-btn>
              </v-layout>
            </v-flex>
          </template>
        </template>

        <template v-if="$route.name === 'ChargeACH'">

          <TerminalSelect ref="terminal" v-model="selectedTerminal"></TerminalSelect>

          <v-flex xs12 sm4>
            <v-layout row wrap justify-end v-if="accountSelected.permissions !== null && accountSelected.permissions.hasOwnProperty('can_use_nacha_authorization') && accountSelected.permissions.can_use_nacha_authorization">
              <v-select
                label="ACH Authorization"
                v-model="fields.ach_authorization"
                :rules="achAuthRules"
                :items="achAuthOptions"
                box
                hide-details
                required
                :error-messages="getRequiredField('ach_authorization')"
              >
                <div slot="append-outer">
                  <Lightbulb medium>
                    <template v-slot:message>
                      <span>
                        For Authorization on File and Print and Sign now Option Transaction will process instantly.
                        <br />
                        For Email Authorization transaction will only process when customer receives email and clicks Authorize.
                      </span>
                    </template>
                  </Lightbulb>
                </div>
              </v-select>
            </v-layout>
          </v-flex>
          <v-flex xs12 sm4>
            <v-layout row wrap justify-end>
              <v-btn @click="validateBeforeProcessing" :disabled="isProcessing">Process</v-btn>
              <v-btn @click="reset" v-if="!parentId">clear</v-btn>
            </v-layout>
          </v-flex>
        </template>
      </v-layout>
    </v-form>

    <v-expansion-panel expand v-model="panel" v-if="debug">
      <v-expansion-panel-content>
        <div slot="header">Responses</div>
        <v-layout column ma-2 v-for="(response, key) in responses" :key="response.time">
          <v-flex wrap>
            <v-toolbar dark dense color="pink">
              <v-toolbar-title>{{ response.time | localeDate }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon>
                <v-icon>fa-close</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card dark class="pr-2 pb-2 pl-2">
              <v-card-text class="ma-0 pa-0">
                <v-tabs dark>
                  <v-tab :href="`#tab-${key}-response`">Response</v-tab>
                  <v-tab :href="`#tab-${key}-request`">Request</v-tab>
                  <v-tab :href="`#tab-${key}-error`">Error</v-tab>
                  <v-tab :href="`#tab-${key}-rawresponse`">Raw Response</v-tab>
                  <v-tab :href="`#tab-${key}-rawrequest`">Raw Request</v-tab>
                  <v-tabs-items style="max-height: 400px; overflow: auto;">
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-response`" :key="`tab-${key}-response`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.response | pretty }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-request`" :key="`tab-${key}-request`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.request | pretty }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-error`" :key="`tab-${key}-error`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.error | pretty }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-rawresponse`" :key="`tab-${key}-rawresponse`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.raw_response }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                    <v-scale-transition>
                      <v-tab-item :id="`tab-${key}-rawrequest`" :key="`tab-${key}-rawrequest`">
                        <v-card light flat style="overflow: auto;">
                          <v-card-text><pre>{{ response.raw_request }}</pre></v-card-text>
                        </v-card>
                      </v-tab-item>
                    </v-scale-transition>
                  </v-tabs-items>
                </v-tabs>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-snackbar
      v-model="snackbar"
      bottom
      :color="snackbarClass"
      :timeout="snackbarTimeout"
    >
      <h2>{{ snackbarMessage }}</h2>
      <v-btn
        flat
        icon
        @click="snackbar = false"
      >
        <v-icon>fa-close</v-icon>
      </v-btn>
    </v-snackbar>

    <v-dialog v-model="waiveHSAConfirmationModal" maxWidth="500px" persistent  @keydown.esc.prevent="closeHSAConfirmationModal" @input="v => v || closeHSAConfirmationModal">
      <v-toolbar dense>
        <v-toolbar-title><v-icon>fa-exclamation-triangle</v-icon> Insurance Adjustment Fee</v-toolbar-title>
      </v-toolbar>

      <v-card>
        <v-card-text fill-height>
          <v-layout row wrap align-center>
            <div>
              <span class="font-weight-bold">Attention:</span>
              <br />
              This feature is for processing insurance payments and FSA or HSA cards with <span class="font-weight-black d-inline">fixed amounts</span>.
            </div>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeHSAConfirmationModal">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="waiveConfirmationModal" maxWidth="500px" persistent>
      <v-toolbar dense>
        <v-toolbar-title><v-icon>fa-exclamation-triangle</v-icon> Price Adjustment Fee</v-toolbar-title>
      </v-toolbar>

      <v-card>
        <v-form ref="formPassword" lazy-validation @submit.prevent @keyup.native.enter="checkWaivePassword">
          <v-card-text fill-height>
            <v-layout row wrap align-center>
              <div class="pa-2">
                <div class="font-weight-bold pb-2">Attention:</div>

                Please be advised that you are about to open the payment page where the price adjustment is waived for your customer.

                <template v-if="domain === 'blueyonder'">
                  By continuing, the amount processed with this transaction will <span class="font-weight-black d-inline">incur a {{ waivedFeeRate }}% fee</span>.

                  This will be billed to you, the merchant, at the end of each month.
                </template>
              </div>
              <template v-if="accountSelected.manager_password && !hasPermission('can_waive_without_password')">
                <div class="pa-2">Please enter the password to waive the price adjustment fee:</div>
                <v-text-field
                  label="Password"
                  v-model="waivePassword"
                  :error-messages="waivePasswordError"
                  append-icon="lock"
                  box
                  type="password"
                ></v-text-field>
              </template>
            </v-layout>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="checkWaivePassword">Confirm</v-btn>
            <v-btn @click="cancelWaiveModal">Cancel</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rerunModal" maxWidth="500px" @keydown.esc.prevent="closeRerunModal()" @input="v => v || closeRerunModal()">
      <v-card>
        <v-card-text class="text-xs-center" fill-height>
          <v-layout row wrap align-center>
            Press the process button below to run the previous transaction without the price adjustment fee
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resultModal" maxWidth="500px" @keydown.esc.prevent="closeResult()" @input="v => v || closeResult()" :transition="false">
      <v-toolbar dense>
        <v-toolbar-title><v-icon v-if="isResponseError">fa-exclamation-triangle</v-icon><v-icon v-if="!isResponseError">fa-check</v-icon> {{ isResponseError ? 'Error' : 'Success' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon flat color="primary" @click="closeResult">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card>
        <v-card-text class="text-xs-center">
          <h3 v-if="isResponseError">{{ responseMessage }}</h3>
          <h4 class="primary--text" v-if="isResponseError && responseMessageDetails">{{ responseMessageDetails }}</h4>
          <h3 v-if="!isResponseError">
            The payment for <span class="title primary--text">${{ (transactionDetails.total_amount / 100).toFixed(2) }}</span> has been <span class="title primary--text" v-if="$route.name === 'ChargeCard'">approved</span><span class="title primary--text" v-if="$route.name === 'ChargeACH'">accepted</span>.

            <div v-if="$route.name === 'ChargeACH' && this.fields.ach_authorization === 'email'"><br />Authorization email sent to <span class="title primary--text">{{ fields.billing_email }}</span>.</div>
          </h3>
        </v-card-text>
        <v-card-actions v-if="!isResponseError">
          <v-layout row wrap align-center justify-space-around>
            <v-btn color="primary" @click="printReceipt" v-if="!isResponseError && ($route.name === 'ChargeCard' || this.fields.ach_authorization !== 'email')">Print Receipt</v-btn>
            <v-btn v-if="!isResponseError && fields.billing_email && ($route.name === 'ChargeCard' || this.fields.ach_authorization !== 'email')" @click="emailReceipt">Email Receipt</v-btn>

            <template v-if="$route.name === 'ChargeACH' && !isResponseError">
              <v-btn color="primary" @click="printAuth" v-if="fields.ach_authorization.startsWith('print')">Print ACH Auth</v-btn>
            </template>

          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmModal
      v-model="showConfirmationModal"
      :amount="priceAdjustmentFee.total_amount"
      :price-adjustment="priceAdjustmentFee.cash_discount"
      :total="priceAdjustmentFee.total_amount_with_cash_discount"
      @confirmed="process"
    ></ConfirmModal>

    <ReceiptPrint ref="receipt" v-bind:transaction="transactionDetails"></ReceiptPrint>
    <AchAuthPrint ref="achAuth" v-bind:transaction="transactionDetails"></AchAuthPrint>

  </v-container>
</template>

<script>
// @TODO Batch cutoff check

import Moment from 'moment'
import VueJsonPrettyWrapper from '@/components/VueJsonPrettyWrapper.vue'

import Bus from '@/bus'
import APIMixin from '@/mixins/api'
import AuthMixin from '@/mixins/auth'
import CashDiscountMixin from '@/mixins/cashDiscount'
import DisplayMixin from '@/mixins/display'
import RecurringMixin from '@/mixins/recurring'
import StoreMixin from '@/store/mixin'
import UtilityMixin from '@/mixins/utility'
import ValidationMixin from '@/mixins/validation'

import AccountTerminalDebug from './components/AccountTerminalDebug.vue'
import AchAuthPrint from '@/components/transactions/AchAuthPrint.vue'
import AchFindInfoModal from '@/components/help/AchFindInfoDialog.vue'
import AmountThreshold from '@/components/terminal/AmountThreshold.vue'
import CountryField from '@/components/forms/CountryDropdown.vue'
import Lightbulb from '@/components/help/Lightbulb'
import ReceiptPrint from '@/components/transactions/ReceiptPrint.vue'
import StateField from '@/components/forms/StateDropdown.vue'
import TerminalSelect from '@/components/terminal/components/TerminalSelect.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'

export default {
  components: {
    AccountTerminalDebug,
    AchAuthPrint,
    AchFindInfoModal,
    AmountThreshold,
    CountryField,
    Lightbulb,
    ReceiptPrint,
    StateField,
    TerminalSelect,
    VueJsonPrettyWrapper,
    ConfirmModal
  },

  mixins: [
    APIMixin,
    AuthMixin,
    CashDiscountMixin,
    DisplayMixin,
    RecurringMixin,
    StoreMixin,
    UtilityMixin,
    ValidationMixin
  ],

  data: function () {
    return {
      // cardMask: '#### - #### - #### - ####',
      responses: [],
      panel: [false],

      snackbar: false,
      snackbarMessage: '',
      snackbarClass: 'success',
      snackbarTimeout: 6000,

      isProcessing: false,
      responseMessage: '',
      responseMessageDetails: '',
      isResponseError: false,

      startDateModal: false,
      endDateModal: false,

      canWaive: false,
      waiveLevel: 0,
      waivePrevious: false,
      waiveHealthCard: false,
      waiveMessage: '',

      waiveHSAConfirmationModal: false,
      waiveConfirmationModal: false,
      waivePasswordPrompt: false,
      waivePassword: '',
      waivePasswordError: [],

      rerunModal: false,
      resultModal: false,
      receiptModal: false,
      showConfirmationModal: false,

      newPaymentInfo: false,
      parentId: 0,
      parentDetails: {},
      parentCustomDetails: {},
      transactionId: 0,
      transactionDetails: {},
      submitNonce: '',

      achTypes: [
        {value: 'CHECKING', text: 'Personal checking'},
        {value: 'SAVINGS', text: 'Personal savings'},
        {value: 'BUSINESSCK', text: 'Business checking'}
      ],

      recurringTypes: [
        {value: 'none', text: 'None'},
        {value: 'daily', text: 'Every day'},
        {value: 'weekly', text: 'Every week'},
        {value: 'biweekly', text: 'Every 2 weeks'},
        {value: 'triweekly', text: 'Every 3 weeks'},
        {value: 'monthly', text: 'Every month'},
        {value: 'bimonthly', text: 'Every 2 months'},
        {value: 'quarterly', text: 'Every 4 months'},
        {value: 'semiannually', text: 'Every 6 months'},
        {value: 'annually', text: 'Every 12 months'}
      ],

      achAuthOptions: [
        {value: 'none', text: 'None'},
        {value: 'onfile', text: 'Authorization on file'},
        {value: 'printsign', text: 'Print and sign now'},
        {value: 'email', text: 'Send authorization email'}
      ],

      nameRules: [
        (v) => !!v || 'Name is required'
      ],
      cardRules: [
        (v) => !!v || 'Card number is required',
        (v) => this.validateCardNumber(v) || 'Card number is invalid'
      ],
      cvvRules: [
        (v) => !!v || 'CVV is required'
      ],
      expirationRules: [
        (v) => !!v || 'Expiration is required'
      ],
      routingRules: [
        (v) => !!v || 'Routing number is required',
        (v) => v.length === 9 || 'Routing number must be nine digits',
        (v) => this.validateABA(v) || 'Routing number is invalid'
      ],
      accountRules: [
        (v) => !!v || 'Account number is required',
        (v) => (v.length < 18) || 'Account number must be less than 18 digits'
      ],
      amountRules: [
        (v) => !!v || 'Amount is required',
        (v) => !isNaN(v) || 'Amount needs to be numeric',
        (v) => (parseFloat(v) <= 500000) || 'Amount must be $500,000 or less'
      ],
      feeRules: [
        (v) => !isNaN(v) || 'Fee needs to be numeric'
      ],
      addressRules: [
        (v) => !!v || 'Address is required'
      ],
      cityRules: [
        (v) => !!v || 'City is required'
      ],
      stateRules: [
        (v) => !!v || 'State is required'
      ],
      postalcodeRules: [
        (v) => !!v || 'Postal code is required'
      ],
      achAuthRules: [
        (v) => (!!v && v !== 'none') || 'Authorization type is required'
      ],
      emailRules: [
        (v) => !v || /.+@.+/.test(v) || 'Email must be valid'
      ],
      startDateRules: [
        // required if recurring
        // today or later
      ],
      endDateRules: [
        // required if recurring
        // day after start date or later
      ],
      passwordRules: [
        (v) => !!v || 'Password is required'
      ],

      current_date: Moment().format('YYYY-MM-DD'),

      selectedTerminal: {},

      fields: this.defaultFields()
    }
  },

  props: {
    paymentType: String
  },

  async created () {
    if (!this.hasPermission('can_use_virtual_terminal')) {
      this.$store.commit('error', 'Access Denied')
      this.$router.go(-1)
    }
  },

  mounted () {
    this.reset()
  },

  destroyed () {
    this.$store.commit('clearErrors', 'timed-out-error-mesasge')
    this.$store.commit('clearInfo')
    this.$store.commit('clearSuccesses')
  },

  computed: {
    environment () {
      return process.env.NODE_ENV
    },

    parentCardNumber () {
      return this.parentDetails.card_bin + ' ****** ' + this.parentDetails.last_four
    },

    parentExpiration () {
      return this.parentDetails.card_expiration.slice(-2) + ' / ' + this.parentDetails.card_expiration.slice(0, 2)
    },

    parentAccountNumber () {
      return '**********' + this.parentDetails.last_four
    },

    cardMask () {
      if (this.cardType === 'amex') {
        return '#### ###### #####'
      } else if (this.cardType === 'diners') {
        return '#### ###### #####'
      } else if (this.cardType === 'discover') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb') {
        return '#### #### #### ####'
      } else if (this.cardType === 'jcb15') {
        return '#### ###### #####'
      } else if (this.cardType === 'maestro') {
        return '#### #### #### ####'
      } else if (this.cardType === 'mastercard') {
        return '#### #### #### ####'
      } else if (this.cardType === 'unionpay') {
        return '#### #### #### ####'
      } else if (this.cardType === 'visa') {
        return '#### #### #### ####'
      }
      return '#### #### #### ####'
    },

    cvvMask () {
      if (this.cardType === 'amex' || this.cardType === 'diners' || this.cardType === 'jcb15') {
        return '####'
      }
      return '###'
    },

    cardType () {
      if (this.parentDetails.payment_type) {
        return this.parentDetails.payment_type
      }

      let amex = /^3[47]\d{0,13}/
      let diners = /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/
      let discover = /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/
      let jcb = /^(?:35\d{0,2})\d{0,12}/
      let jcb15 = /^(?:2131|1800)\d{0,11}/
      let maestro = /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/
      let mastercard = /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/
      let unionpay = /^62\d{0,14}/
      let visa = /^4\d{0,15}/

      if (amex.test(this.fields.card_number)) {
        return 'amex'
      } else if (diners.test(this.fields.card_number)) {
        return 'diners'
      } else if (discover.test(this.fields.card_number)) {
        return 'discover'
      } else if (jcb.test(this.fields.card_number)) {
        return 'jcb'
      } else if (jcb15.test(this.fields.card_number)) {
        return 'jcb15'
      } else if (maestro.test(this.fields.card_number)) {
        return 'maestro'
      } else if (mastercard.test(this.fields.card_number)) {
        return 'mastercard'
      } else if (unionpay.test(this.fields.card_number)) {
        return 'unionpay'
      } else if (visa.test(this.fields.card_number)) {
        return 'visa'
      }
      return 'unknown'
    },

    amount () {
      return this.fields.amount
    },

    waive () {
      return this.fields.waive_fee
    },

    cardTerminalList: function () {
      let terminals = [
        {terminal_id: 0, name: '- Please Select -'}
      ]
      return terminals.concat(this.terminalsCardCanCharge)
    },
    achTerminalList: function () {
      let terminals = [
        {terminal_id: 0, name: '- Please Select -'}
      ]
      return terminals.concat(this.terminalsACHCanCharge)
    },

    formattedRecurringStartDate () {
      return this.fields.recurring_start_date ? Moment(this.fields.recurring_start_date).format('MMM-DD-YYYY') : ''
    },

    formattedRecurringEndDate () {
      return this.fields.recurring_end_date ? Moment(this.fields.recurring_end_date).format('MMM-DD-YYYY') : ''
    },

    minimumRecurringStartDate: function () {
      return Moment().format('YYYY-MM-DD')
    },

    minimumRecurringEndDate: function () {
      return Moment(this.fields.recurring_start_date, 'YYYY-MM-DD').add(2, 'days').format('YYYY-MM-DD')
    },

    recurring: function () {
      return this.fields.recurring
    },

    recurringEndDate: function () {
      return this.fields.recurring_end_date
    },

    priceAdjustmentFee: function () {
      if (this.fields.waive_fee) {
        return ''
      }
      return {
        total_amount: this.fields.amount,
        cash_discount: this.calculateCashDiscountFee(this.fields.amount),
        total_amount_with_cash_discount: this.fields.amount + (parseFloat(this.calculateCashDiscountFee(this.fields.amount)))
      }
    },

    recurringOngoing: function () {
      return this.fields.recurring_ongoing
    },

    waivedFeeRate: function () {
      if (this.accountSelected.hasOwnProperty('permissions') && this.accountSelected.permissions !== null && this.accountSelected.permissions.hasOwnProperty('waived_fee_rate')) {
        return this.accountSelected.permissions.waived_fee_rate
      }

      if (this.domain === 'blueyonder') {
        return 3.2
      }

      if (this.selectedTerminal.hasOwnProperty('cashdiscount_schedule')) {
        return this.calculateRate(this.selectedTerminal)
      }

      return 0
    },

    waivedHSAFeeRate: function () {
      if (this.accountSelected.hasOwnProperty('permissions') && this.accountSelected.permissions !== null && this.accountSelected.permissions.hasOwnProperty('waived_hsa_fee_rate')) {
        return this.accountSelected.permissions.waived_hsa_fee_rate
      }
      return 2.95
    }
  },

  filters: {
    pretty (data) {
      return JSON.stringify(JSON.parse(JSON.stringify(data)), null, 2)
    },
    localeDate (data) {
      return new Date(data).toLocaleString()
    }
  },

  watch: {
    '$route' (to, from) {
      this.reset()
    },
    amount () {
      this.calculateFees()
    },
    waive () {
      this.calculateFees()
    },
    recurring (val) {
      this.calculatePaymentCount()
    },
    recurringStartDate (val) {
      this.$set(this.fields, 'recurring_end_date', this.calculateEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_payments))
    },
    recurringEndDate (val) {
      this.calculatePaymentCount()
    },
    recurringOngoing () {
      this.calculatePaymentCount()
    }
  },

  methods: {
    isFormValid () {
      if (this.$refs.hasOwnProperty('form')) {
        return this.$refs.form.validate()
      }

      return false
    },

    defaultFields () {
      return {
        method: null,
        amount: 0.00,
        fee: 0.00,
        fee_waived: 0.00,
        percentage: 0,
        token: null,

        card_name: '',
        card_number: '',
        card_cvv: null,
        card_expiration: '',

        recurring: 'none',
        recurring_start_date: Moment().format('YYYY-MM-DD'),
        recurring_end_date: '',
        recurring_payments: 0,
        recurring_ongoing: false,

        ach_routing_number: '',
        ach_account_number: '',
        ach_check_number: null,
        ach_type: 'CHECKING',
        ach_authorization: 'none',

        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_postalcode: '',
        billing_country: 'USA',
        billing_phone: null,
        billing_email: null,
        customer_po: null,

        custom_field_1: null,
        custom_field_2: null,
        custom_field_3: null,
        notes: null,
        tokenize: true,
        card_terminal_id: null,
        ach_terminal_id: null,
        waive_fee: false
      }
    },

    getRequiredField (field) {
      if (!this.fields.hasOwnProperty(field)) {
        return 'Required'
      }

      if (field === 'ach_authorization') {
        if (this.fields[field] && this.fields[field] !== 'none') {
          return ''
        }
      } else if (this.fields[field]) {
        return ''
      }

      return 'Required'
    },

    async validateBeforeProcessing () {
      if (!this.$refs.form.validate()) {
        let errors = []
        this.$refs.form.inputs.forEach(e => {
          if (e.hasError) {
            errors.push(e.errorBucket[0])
          }
        })

        let message = 'Validation Error'

        if (errors.length > 1) {
          message = 'Validation Errors'
        }

        Bus.$emit('openModal', {
          status: 'error',
          message: message,
          messageDetails: errors
        })

        return false
      } else {
        this.showConfirmModal()
      }
    },

    async process () {
      try {
        let options = { ...this.fields }

        if (!this.selectedTerminal.terminal_id) {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Please select a terminal'
          })

          return false
        }
        options.terminal_id = this.selectedTerminal.terminal_id

        if (this.parentId && this.parentDetails.token) {
          options.token = this.parentDetails.token
        }

        if (this.fields.recurring_ongoing) {
          options.recurring_end_date = '2099-12-31'
        }

        if (this.$route.name === 'ChargeCard') {
          delete options.ach_terminal_id
          delete options.ach_routing_number
          delete options.ach_account_number
          delete options.ach_check_number
          delete options.ach_type
          delete options.ach_authorization
          options.request_type = 'sale'
          // options.terminal_id = options.card_terminal_id
          options.card_month = options.card_expiration.slice(0, 2)
          options.card_year = options.card_expiration.slice(-2)
          options.health_rewards_card = this.waiveHealthCard
          // delete options.card_expiration
        } else if (this.$route.name === 'ChargeACH') {
          if (options.ach_authorization === 'email' && !options.billing_email) {
            Bus.$emit('openModal', {
              status: 'error',
              message: 'Email required for ACH authorization'
            })
            return false
          }

          delete options.card_terminal_id
          delete options.card_number
          delete options.card_cvv
          delete options.card_expiration
          delete options.fee
          options.request_type = 'ach'
          // options.terminal_id = options.ach_terminal_id
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Cannot determine payment type'
          })
          return false
        }

        if (!options.card_name && this.parentDetails.billing_name) {
          options.card_name = this.parentDetails.billing_name
        }

        options.nonce = this.submitNonce

        // send account/terminal information
        this.isProcessing = true
        Bus.$emit('openWaitModal')
        let result = await this.api({
          method: 'post',
          url: `/terminal/payment`,
          data: options
        })
        Bus.$emit('closeWaitModal')

        if (result.data.response.ResponseText === 'APPROVED') {
          this.transactionDetails = result.data.response.Transaction
          this.responseMessage = 'Payment Approved'
          this.resultModal = true
        } else if (result.data.response.ResponseText === 'PENDING') {
          this.transactionDetails = result.data.response.Transaction
          this.responseMessage = 'Scheduled Payment Approved'
          this.resultModal = true
        } else if (result.data.response.ResponseText === 'CHECK ACCEPTED') {
          this.transactionDetails = result.data.response.Transaction
          this.responseMessage = 'Payment Accepted'
          this.resultModal = true
        } else {
          let error = result.data.response.ResponseText
          if (result.data.response.ErrMsg) {
            error += ': ' + result.data.response.ErrMsg
          }
          this.transactionDetails = {}
          this.isResponseError = true
          this.responseMessage = error
          this.responseMessageDetails = ''
          this.isProcessing = false

          if (error === 'DECLINED' && this.$route.name === 'ChargeCard') {
            this.responseMessageDetails = 'Please verify card data including the expiration and CVV'
          }

          if (result.data.response.Transaction && result.data.response.Transaction.hasOwnProperty('error_messages') && result.data.response.Transaction.error_messages) {
            let messages = JSON.parse(result.data.response.Transaction.error_messages)

            // show detailed error as long as it isn't the generic 'do not honor'
            if (messages.length > 0 && messages[0] !== 'TRANS DENIED DO NOT HONOR' && messages[0] !== error) {
              this.responseMessageDetails = messages.join()
            }
          }
        }

        // user wants to adjust an installment, usually adding a payment
        // if all is successful, cancel all the previous
        if ((result.data.response.ResponseText === 'APPROVED' ||
          result.data.response.ResponseText === 'PENDING' ||
          result.data.response.ResponseText === 'CHECK ACCEPTED') &&
          (!this.parentDetails.hasOwnProperty('settled') ||
          (this.parentDetails.hasOwnProperty('settled') &&
          !this.parentDetails.settled)) &&
          this.$route.query.rerun) {
          // void / cancel recurring
          let options = {
            terminal_id: this.parentDetails.terminal_id,
            transaction_id: this.parentDetails.id
          }

          Bus.$emit('openWaitModal')
          let result = await this.api({
            method: 'post',
            url: `/terminal/void`,
            data: options
          })
          Bus.$emit('closeWaitModal')

          if (result.data) {
            if (result.data.response.ActionCode === '000') {
              if (this.responseMessageDetails) {
                this.responseMessageDetails += '. '
              }
              this.responseMessageDetails += 'Original transaction cancelled'
            } else {
              if (this.responseMessageDetails) {
                this.responseMessageDetails += '. '
              }
              this.responseMessageDetails += 'Problem cancelling original transaction'
            }
          } else {
            if (this.responseMessageDetails) {
              this.responseMessageDetails += '. '
            }
            this.responseMessageDetails += 'Problem cancelling original transaction'
          }
        }

        this.resultModal = true
        this.responses.unshift(result.data)
        this.panel = [true]
      } catch (err) {
        this.isProcessing = false
        this.submitNonce = this.generateId(36)
        Bus.$emit('closeWaitModal')
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      }
    },

    async reset (opts) {
      this.newPaymentInfo = false
      this.waiveHSAConfirmationModal = false
      this.waiveConfirmationModal = false
      this.rerunModal = false
      this.resultModal = false
      this.receiptModal = false

      this.$refs.form.resetValidation()

      this.$store.commit('clearErrors', 'timed-out-error-mesasge')
      this.$store.commit('clearInfo')
      this.$store.commit('clearSuccesses')

      this.panel = [false]

      this.snackbar = false
      this.snackbarMessage = ''
      this.snackbarClass = ''

      this.isProcessing = false
      this.responseMessage = ''
      this.responseMessageDetails = ''
      this.isResponseError = false

      this.startDateModal = false
      this.endDateModal = false

      this.parentId = 0
      this.parentDetails = {}
      this.parentCustomDetails = {}
      this.transactionId = 0
      this.transactionDetails = {}

      this.canWaive = false
      this.waiveLevel = 0
      this.waivePrevious = false
      this.waiveHealthCard = false

      this.selectedTerminal = {}
      this.$refs.terminal.reset()

      this.submitNonce = this.generateId(36)

      this.fields = this.defaultFields()

      // @TODO Handle default terminals (need to handle Card vs ACH separately)
      if (this.$route.name === 'ChargeCard' && this.terminalsCardCanCharge && this.terminalsCardCanCharge.length) {
        if (this.myProfile.custom.hasOwnProperty('default_card_terminal_id')) {
          this.terminal = this.myProfile.custom.default_card_terminal_id
        } else {
          this.terminal = this.terminalsCardCanCharge[0].terminal_id
        }
      } else if (this.$route.name === 'ChargeACH' && this.terminalsACHCanCharge && this.terminalsACHCanCharge.length) {
        if (this.myProfile.custom.hasOwnProperty('default_ach_terminal_id')) {
          this.terminal = this.myProfile.custom.default_ach_terminal_id
        } else {
          this.terminal = this.terminalsACHCanCharge[0].terminal_id
        }
      }

      if (this.$route.query.hasOwnProperty('waive') && this.$route.query.waive === '1') {
        if (!this.hasPermission('can_waive_card_fees')) {
          this.$router.go(-1)
        } else {
          this.canWaive = true
          this.fields.waive_fee = true
          this.waiveLevel = 1
          this.waivePasswordPrompt = true
          this.waiveConfirmationModal = true
          this.waiveMessage = 'WAIVED PRICE ADJUSTMENT'
        }
      }

      if (this.$route.query.hasOwnProperty('waive') && this.$route.query.waive === '2') {
        if (!this.hasPermission('can_waive_health_card_fees')) {
          this.$router.go(-1)
        } else {
          this.canWaive = true
          this.waiveHealthCard = true
          this.waiveLevel = 2
          this.fields.waive_fee = true
          this.waiveHSAConfirmationModal = true
          this.waiveMessage = 'Insurance / HSA / FSA'
        }
      }

      if (this.$route.query.transaction || this.$route.query.rerun) {
        if (this.$route.query.transaction) {
          this.transactionId = this.$route.query.transaction
        } else {
          this.transactionId = this.$route.query.rerun
        }

        let data = await this.api({url: `/transactions/${this.transactionId}`})

        this.parentDetails = data.data.transaction
        if (!this.parentDetails.billing_name && this.parentDetails.card_name) {
          this.parentDetails.billing_name = this.parentDetails.card_name
        }
        this.parentCustomDetails = JSON.parse(data.data.transaction.custom)
        this.parentId = data.data.transaction.id

        this.fields.customer_po = this.parentDetails.customer_po
        this.fields.custom_field_1 = this.parentDetails.custom_field_1
        this.fields.custom_field_2 = this.parentDetails.custom_field_2
        this.fields.custom_field_3 = this.parentDetails.custom_field_3
        this.fields.notes = this.parentDetails.notes

        this.fields.billing_address = this.parentDetails.billing_address
        this.fields.billing_city = this.parentDetails.billing_city
        this.fields.billing_state = this.parentDetails.billing_state
        this.fields.billing_postalcode = this.parentDetails.billing_postal_code
        this.fields.billing_country = this.parentDetails.billing_country
        this.fields.billing_phone = this.parentDetails.billing_phone
        this.fields.billing_email = this.parentDetails.billing_email

        this.fields.ach_authorization = 'onfile'

        // Set terminal to parent if possible
        // this.terminal = this.parentDetails.terminal_id
        this.$refs.terminal.reset(this.parentDetails.terminal_id)

        if (this.$route.query.transaction) {
          if (typeof this.parentCustomDetails === 'object' && this.parentCustomDetails !== null && this.parentCustomDetails.hasOwnProperty('is_health_rewards_card') && this.parentCustomDetails.is_health_rewards_card) {
            this.canWaive = true
            this.waiveHealthCard = true
            this.waiveLevel = 2
            this.fields.waive_fee = true
            this.waiveHSAConfirmationModal = true
            this.waiveMessage = 'Insurance / HSA / FSA'
          }
        } else if (this.$route.query.rerun) {
          // this.fields.waive_fee = true
          // this.canWaive = true
          // this.waivePrevious = true

          // // this.$store.commit('info', 'Press the process button below to run the previous transaction without the price adjustment fee')

          // Bus.$emit('openModal', {
          //   status: 'info',
          //   message: 'Press the process button to rerun the transaction without the price adjustment fee'
          // })

          this.fields.amount = ((this.parentDetails.total_amount - this.parentDetails.fee_amount - this.parentDetails.tax_amount) / 100).toFixed(2)
          // this.fields.fee_amount = 0.00
          // this.fields.fee = '0.00'

          this.fields.recurring = this.parentDetails.modify_recurring_type ? this.parentDetails.modify_recurring_type : this.parentDetails.recurring_type
          this.fields.recurring_start_date = this.parentDetails.modify_recurring_start ? Moment.utc(this.parentDetails.modify_recurring_start).format('YYYY-MM-DD') : Moment.utc(this.parentDetails.recurring_start).format('YYYY-MM-DD')

          if (this.parentDetails.modify_recurring_end) {
            this.fields.recurring_end_date = Moment.utc(this.parentDetails.modify_recurring_end).format('YYYY-MM-DD') === '2099-12-31' ? '' : Moment.utc(this.parentDetails.modify_recurring_end).format('YYYY-MM-DD')
            this.fields.recurring_ongoing = !!(Moment.utc(this.parentDetails.modify_recurring_end).format('YYYY-MM-DD') === '2099-12-31')
          } else if (this.parentDetails.is_recurring && !this.parentDetails.recurring_end) {
            this.fields.recurring_ongoing = true
          } else {
            this.fields.recurring_end_date = Moment.utc(this.parentDetails.recurring_end).format('YYYY-MM-DD') === '2099-12-31' ? '' : Moment.utc(this.parentDetails.recurring_end).format('YYYY-MM-DD')
            this.fields.recurring_ongoing = !!(Moment.utc(this.parentDetails.recurring_end).format('YYYY-MM-DD') === '2099-12-31')
          }
        }
      }

      this.$refs.form.validate()
    },

    clearToken () {
      this.fields.card_name = this.parentDetails.billing_name
      this.parentId = 0
      this.parentDetails = {}
      this.parentCustomDetails = {}
      this.newPaymentInfo = !!this.newPaymentInfo

      if (!this.newPaymentInfo) {
        this.reset()
      }
    },

    closeRerunModal () {
      this.rerunModal = false
    },

    closeHSAConfirmationModal () {
      this.waiveHSAConfirmationModal = false
    },

    cancelWaiveModal () {
      this.$router.go(-1)
    },

    closeResult () {
      if (!this.isResponseError) {
        if (Object.keys(this.$route.query).length) {
          this.$router.replace('/credit')
        }

        this.reset()
      } else {
        this.resultModal = false

        this.snackbar = false
        this.snackbarMessage = ''
        this.snackbarClass = ''

        this.isProcessing = false
        this.responseMessage = ''
        this.responseMessageDetails = ''
        this.isResponseError = false
      }
    },

    calculateFees () {
      if (this.$route.name !== 'ChargeCard') {
        this.fields.fee = 0.00
        this.fields.fee_waived = 0.00
      } else if (this.fields.waive_fee) {
        this.fields.fee = 0.00
        this.fields.fee_waived = this.calculateFee(this.selectedTerminal, this.fields.amount)
      } else {
        this.fields.fee = this.calculateFee(this.selectedTerminal, this.fields.amount)
        this.fields.fee_waived = 0.00
      }
    },

    recurringAllowedEndDates (date) {
      if (!this.fields.recurring || this.fields.recurring === 'none') {
        return false
      }

      if (!this.fields.recurring_start_date) {
        return false
      }

      return this.allowedEndDates(this.fields.recurring, date, this.fields.recurring_start_date)
    },

    calculatePaymentCount () {
      if (this.fields.recurring === 'none') {
        return
      }
      if (this.fields.recurring_start_date === '') {
        return
      }
      if (this.fields.recurring_end_date === '') {
        return
      }

      if (this.fields.recurring_ongoing) {
        this.fields.recurring_end_date = ''
        this.fields.recurring_payments = 0
        return
      }

      let startDate = Moment(this.fields.recurring_start_date)
      let endDate = Moment(this.fields.recurring_end_date)

      if (this.fields.recurring === 'daily') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'days') + 1)
      } else if (this.fields.recurring === 'weekly') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'weeks') + 1)
      } else if (this.fields.recurring === 'biweekly') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'weeks') / 2 + 1)
      } else if (this.fields.recurring === 'triweekly') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'weeks') / 3 + 1)
      } else if (this.fields.recurring === 'monthly') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'months') + 1)
      } else if (this.fields.recurring === 'bimonthly') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'months') / 2 + 1)
      } else if (this.fields.recurring === 'quarterly') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'months') / 4)
      } else if (this.fields.recurring === 'semiannually') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'months') / 6 + 1)
      } else if (this.fields.recurring === 'annually') {
        this.fields.recurring_payments = Math.trunc(endDate.diff(startDate, 'years') + 1)
      }
    },

    handleChange (field) {
      if (field === 'payments') {
        // calculate end date
        this.$set(this.fields, 'recurring_end_date', this.calculateEndDate(this.fields.recurring, this.fields.recurring_start_date, this.fields.recurring_payments))
      }
    },

    printReceipt () {
      this.$refs.receipt.print()
    },

    printAuth () {
      this.$refs.achAuth.print()
    },

    async emailReceipt () {
      try {
        if (!this.transactionDetails || !this.transactionDetails.id) {
          throw new Error('Unable to identify transaction')
        }

        let result = await this.api({
          method: 'get',
          url: `/transactions/${this.transactionDetails.id}/send_receipt_email`
        })

        if (result.data && result.data.error === false) {
          Bus.$emit('openModal', {
            status: 'success',
            message: 'Email Sent'
          })
        } else {
          Bus.$emit('openModal', {
            status: 'error',
            message: 'Unexpected Error'
          })
        }
      } catch (err) {
        Bus.$emit('openModal', {
          status: 'error',
          message: err
        })
      }
    },

    // @TODO exports.validateManagerPassword = async (user_id, account_id, token_id, password) => {
    checkWaivePassword () {
      if (this.accountSelected.manager_password && !this.hasPermission('can_waive_without_password') && this.accountSelected.manager_password !== this.waivePassword) {
        this.waivePasswordError = ['Invalid Password']
      } else {
        this.waivePasswordError = []
        this.waiveConfirmationModal = false
      }
    },

    calculateCashDiscountFee (amount) {
      if (this.fields.waive_fee) {
        return 0
      }

      if (this.$route.name !== 'ChargeCard') {
        return 0
      }

      return this.calculateFee(this.selectedTerminal, amount)
    },

    showConfirmModal () {
      this.showConfirmationModal = true
    }

  }
}
</script>



// WEBPACK FOOTER //
// src/components/terminal/Basic.vue